import type { ReviewTicker } from "@eu.mdotm.api/sphere";
import { reportPlatformError } from "$root/api/error-reporting";
import { hasAccess } from "$root/components/AuthorizationGuard";
import { useCrumbs } from "$root/components/Crumbs/useCrumbs";
import { LeavePrompt } from "$root/components/LeavePrompt";
import { PageHeader } from "$root/components/PageHeader";
import { typedUrlForRoute, useTypedNavigation } from "$root/components/PlatformRouter/RoutesDef";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import { type CompareDataItem, CompareOverlay } from "$root/functional-areas/compare-portfolio/CompareOverlay";
import {
	INVESTMENT_INSTRUMENT_LIMIT,
	MIN_NUMBER_OF_INSTRUMENTS,
	MIN_NUMBER_OF_INVESTMENT_INSTRUMENTS,
} from "$root/functional-areas/const";
import {
	cashTickerToInstrument,
	covertInstrumentEditorToReviewTicker,
	useInstrumentEditorBuilder,
} from "$root/functional-areas/instruments-editor/builder";
import type { Instrument } from "$root/functional-areas/instruments-editor/const";
import InstrumentCompositionEditor from "$root/functional-areas/instruments-editor/instrumentEditor";
import ExposureSankeyBlock from "$root/functional-areas/instruments-editor/InstrumentExposureSankey";
import { createInstrumentList, instrumentsRepository } from "$root/functional-areas/instruments/repository";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import { customerDecimalPlaces, useUserValue } from "$root/functional-areas/user";
import { weightTotalStatus } from "$root/functional-areas/instruments-editor/weight-total";
import { platformToast } from "$root/notification-system/toast";
import { trackMixPanelEvent } from "$root/third-party-integrations/initMixPanel";
import { useMappedQueries, useMappedQuery, useQueryReactor } from "$root/third-party-integrations/react-query/hooks";
import { identity } from "@mdotm/utils/functions";
import { Button } from "@mdotm/shadcn/mdotm/components";
import { useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { Map, OrderedMap } from "immutable";
import { useMemo, useState } from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import { instrumentsEditorRepository } from "$root/functional-areas/instruments-editor/repository";
import { countInstrumentInComposition, isInstrumentCountValid } from "../common";
import { generateDefaultManualCreation } from "./draftDefaults";
import { spawnSubmitManualCreationDialog } from "./spawn-createPortfolio";
import { columnsDefinition } from "$root/ui-lib/table/column";
import { userClassifications$ } from "$root/functional-areas/instruments/user-classifications";
import { useAtomValue } from "@mdotm/shadcn/legacy/reactivity";
import { usePortfolioStudioRedirect } from "$root/pages/PortfoliosStudio/usePortfolioStudioRedirect";
import { QuantityManualCreation } from "$root/functional-areas/instruments-editor/quantity/creation/QuantityManualCreation";

async function submitWithTryCath(callback: () => Promise<void>) {
	try {
		await callback();
	} catch (error) {
		const e = error as AxiosError<{ message?: string }>;
		const message = e.response?.data.message;

		if (message === "maxValue should be greater or equal to 0.02") {
			platformToast({
				children: "Please review your instrument, minimum weight acceptable is 0.02",
				severity: "error",
				icon: "Portfolio",
			});
		}

		if (message !== "maxValue should be greater or equal to 0.02") {
			platformToast({
				children: "Something went wrong, please try later",
				severity: "error",
				icon: "Portfolio",
			});
		}

		reportPlatformError(error, "ERROR", "portfolio", { message: "unable to create portfolio from upload editor" });
		throw error;
	}
}

function ManualCreation({
	workspaceUid,
	uuid,
	preselectedInvestments,
	intentId,
}: {
	workspaceUid?: string;
	uuid?: string;
	preselectedInvestments?: string;
	intentId?: string;
}): React.JSX.Element {
	const [expandExposure, setExpandExposure] = useState(false);
	const [pathToNotBlock, setPathToNotBlock] = useState<string[]>([typedUrlForRoute("Login", {})]);

	const { t } = useTranslation();
	const { push } = useTypedNavigation();
	const user = useUserValue();

	const querySummary = useQuery({
		...portfolioRepository.summary.getQueryConfig(uuid ?? "", { enhanced: false }),
		enabled: Boolean(uuid),
	});

	const { data: summary } = querySummary;
	const instrumentBuilder = useInstrumentEditorBuilder({ composition: Map() });

	const queryEditCompositionRaw = uuid
		? instrumentsRepository.editComposition.useQuery(uuid, "INVESTMENT_DRAFT")
		: instrumentsRepository.newComposition.useQuery("INVESTMENT");

	const queryEditComposition = useMappedQuery(
		(editComposition) => {
			const { composition } = editComposition;
			const investmentsToAdd = preselectedInvestments
				? preselectedInvestments
						?.split(",")
						.map((ticker): ReviewTicker => ({ ticker, proxyOverwriteType: "PORTFOLIO_MIXED" }))
				: [];
			if (investmentsToAdd.length === 0) {
				return editComposition;
			}
			return { ...editComposition, composition: [...investmentsToAdd, ...(composition ?? [])] };
		},
		[preselectedInvestments],
		queryEditCompositionRaw,
	);

	const queryUserInstrumentMap = useQuery({
		...instrumentsRepository.userClassifications.getQueryConfig(
			queryEditComposition.data?.composition?.map((entry) => entry.ticker).filter((ticker) => ticker != null) ?? [],
		),
		enabled: queryEditComposition.data != null,
	});

	const queryInvestmentsRichInfo = useQuery({
		...instrumentsRepository.investmentsRichInfo.getQueryConfig(queryEditComposition.data?.composition ?? []),
		enabled: queryEditComposition.data != null,
	});

	const query = useMappedQueries(
		identity,
		[],
		[queryEditComposition, queryUserInstrumentMap, queryInvestmentsRichInfo],
	);

	useQueryReactor(query, {
		onSuccess([compositionWithCash, userInstrumentsMap, investmentsRichInfo]) {
			const instruments = createInstrumentList({
				composition: compositionWithCash.composition ?? [],
				userInstrumentsMap,
				investmentData: investmentsRichInfo,
			});

			instrumentBuilder.reset(
				{
					composition: OrderedMap<string, Instrument>(instruments.map((x) => [x.id, x])),
					cash: compositionWithCash.cashTicker ? cashTickerToInstrument(compositionWithCash.cashTicker) : undefined,
				},
				{ disableToggleDirty: true },
			);
			const someInvestmentInComposition = instruments.find((x) => x.instrumentType === "INVESTMENT");
			if (someInvestmentInComposition) {
				setExpandExposure(true);
			}
		},
	});

	const comparedInvestments = useMemo((): CompareDataItem[] => {
		const investmentsCompared = instrumentBuilder
			.getComposition()
			.filter((x) => instrumentBuilder.getComparedInstrument(x.id) !== undefined)
			.values();

		return Array.from(investmentsCompared).map((instrument) => ({
			id: instrument.id,
			composition:
				instrument.macroAssetClassExposure?.map((x) => ({
					quality: x.firstQualityLevel,
					weight: x.weight,
				})) ?? [],
			numberOfInstrument: instrument.nofInstruments ?? 0,
			portfolioName: instrument.name ?? "-",
			uuid: instrument.investmentUuid,
			note: instrument.lastActionNote,
			action: instrument.action,
		}));
	}, [instrumentBuilder]);

	const compositionMapWithoutDeleted = useMemo(
		() => instrumentBuilder.getComposition({ removeDeleted: true }),
		[instrumentBuilder],
	);
	const maxInstrumentsInComposition = hasAccess(user, { requiredService: "NUMBER_OF_INSTRUMENTS_CHECK_BYPASS" })
		? +Infinity
		: INVESTMENT_INSTRUMENT_LIMIT;

	const minInstrumentsInComposition = hasAccess(user, { requiredService: "NUMBER_OF_INSTRUMENTS_CHECK_BYPASS" })
		? MIN_NUMBER_OF_INSTRUMENTS
		: MIN_NUMBER_OF_INVESTMENT_INSTRUMENTS;

	const crumbs = useCrumbs();
	const workspacePath = useCrumbs().useWorkspacePath(workspaceUid);
	const classifications = useAtomValue(userClassifications$) ?? [];
	const portfolioStudio = usePortfolioStudioRedirect(workspaceUid);
	const initialColumns = useMemo(() => columnsDefinition.instrumentComposition(classifications), [classifications]);
	return (
		<>
			<PageHeader
				crumbs={[
					...crumbs.portfolioStudio("Portfolios", summary?.uuid),
					...workspacePath,
					...(summary ? crumbs.portfolioName(summary) : crumbs.newPortfolio),
				]}
				titleAction={
					<div className="flex items-center space-x-2">
						<Button
							size="small"
							onClick={() => portfolioStudio.navigate()}
							palette="tertiary"
							data-qualifier="CompositionEditor/Cancel"
						>
							{t("BUTTON.CANCEL")}
						</Button>

						<Button
							size="small"
							onClick={() =>
								spawnSubmitManualCreationDialog(
									summary
										? {
												entity: "INVESTMENT_DRAFT",
												defaultManualCreation: generateDefaultManualCreation(summary),
												includesInvestments: instrumentBuilder
													.getComposition()
													.some((instrument) => instrument.proxyOverwriteType === "PORTFOLIO_MIXED"),
												onAsyncSubmit: async (form) => {
													await submitWithTryCath(async () => {
														const composition = covertInstrumentEditorToReviewTicker(
															Array.from(instrumentBuilder.getComposition({ removeDeleted: true }).values()),
														);

														if (uuid && form.submitMode === "save-as-draft") {
															await instrumentsEditorRepository.draft.update({
																form,
																composition,
																uuid: uuid,
															});
															platformToast({
																children: `Portfolio ${form.name} saved successfully`,
																severity: "success",
																icon: "Portfolio",
															});
															return;
														}

														const nOfInstrumentsInComposition = countInstrumentInComposition(
															compositionMapWithoutDeleted,
															form.portfolioSavingMode ?? "NEST_PORTFOLIOS",
														);

														if (form.submitMode === "save-as-portfolio") {
															if (nOfInstrumentsInComposition < minInstrumentsInComposition) {
																platformToast({
																	children: `You must add at least ${minInstrumentsInComposition} instruments to your composition before submitting. Please include more instruments to proceed.`,
																	severity: "warning",
																	icon: "Portfolio",
																});
																return;
															}

															const canSubmit =
																weightTotalStatus(instrumentBuilder.getTotal("weight"), customerDecimalPlaces(user)) ===
																	"exact" &&
																isInstrumentCountValid(nOfInstrumentsInComposition, {
																	maxInstrumentsInComposition,
																	minInstrumentsInComposition,
																});
															if (!canSubmit) {
																platformToast({
																	children: `Unable to generate your Portfolio please review your composition`,
																	severity: "warning",
																	icon: "Portfolio",
																});
																return;
															}

															const { identifier } = await instrumentsEditorRepository.draft.saveAsInvestment({
																form,
																composition,
																uuid: uuid!,
															});

															flushSync(() =>
																setPathToNotBlock([
																	typedUrlForRoute("PortfolioDetails", { portfolioUid: identifier! }),
																]),
															);
															if (workspaceUid) {
																push("PortfolioDetailsWithWorkspace", {
																	workspaceUid: workspaceUid,
																	portfolioUid: identifier!,
																});
															} else {
																push("PortfolioDetails", { portfolioUid: identifier! });
															}
														}
													});
												},
											}
										: {
												entity: "INVESTMENT",
												includesInvestments: instrumentBuilder
													.getComposition()
													.some((instrument) => instrument.proxyOverwriteType === "PORTFOLIO_MIXED"),
												onAsyncSubmit: async (form) => {
													await submitWithTryCath(async () => {
														const composition = covertInstrumentEditorToReviewTicker(
															Array.from(instrumentBuilder.getComposition({ removeDeleted: true }).values()),
														);

														if (form.submitMode === "save-as-draft") {
															const { identifier } = await instrumentsEditorRepository.create.investmentDraft({
																form,
																composition,
															});
															trackMixPanelEvent("portfolio.entityActions.createAsDraftManual", {
																intentId,
																portfolio: {
																	uuid: identifier!,
																	name: form.name!,
																},
															});
															flushSync(() =>
																setPathToNotBlock([
																	typedUrlForRoute("Portfolios/ManualPortfolioCreation", { uuid: identifier }),
																]),
															);
															platformToast({
																children: `Portfolio ${form.name} saved successfully`,
																severity: "success",
																icon: "Portfolio",
															});
															push("Portfolios/ManualPortfolioCreation", { uuid: identifier });
														}
														const nOfInstrumentsInComposition = countInstrumentInComposition(
															compositionMapWithoutDeleted,
															form.portfolioSavingMode ?? "NEST_PORTFOLIOS",
														);

														if (form.submitMode === "save-as-portfolio") {
															if (nOfInstrumentsInComposition < minInstrumentsInComposition) {
																platformToast({
																	children: `You must add at least ${minInstrumentsInComposition} instruments to your composition before submitting. Please include more instruments to proceed.`,
																	severity: "warning",
																	icon: "Portfolio",
																});
																return;
															}

															const canSubmit =
																weightTotalStatus(instrumentBuilder.getTotal("weight"), customerDecimalPlaces(user)) ===
																	"exact" &&
																isInstrumentCountValid(nOfInstrumentsInComposition, {
																	maxInstrumentsInComposition,
																	minInstrumentsInComposition,
																});
															if (!canSubmit) {
																platformToast({
																	children: `Unable to generate your Portfolio please review your composition`,
																	severity: "warning",
																	icon: "Portfolio",
																});
																return;
															}
															const { identifier } = await instrumentsEditorRepository.create.investment({
																form,
																composition,
															});
															trackMixPanelEvent("portfolio.entityActions.createAsPortfolioManual", {
																intentId,
																portfolio: {
																	uuid: identifier!,
																	name: form.name!,
																},
															});

															flushSync(() =>
																setPathToNotBlock([
																	typedUrlForRoute("PortfolioDetails", { portfolioUid: identifier! }),
																]),
															);
															push("PortfolioDetails", { portfolioUid: identifier! });
														}
													});
												},
											},
								)
							}
							palette="primary"
							data-qualifier="CompositionEditor/Save"
						>
							{t("BUTTON.DONE")}
						</Button>
					</div>
				}
			/>
			<ReactQueryWrapperBase query={query}>
				{() => (
					<>
						<div className="min-h-0 grow flex">
							{uuid ? (
								<InstrumentCompositionEditor
									entity="INVESTMENT_DRAFT"
									mode="edit"
									uuid={uuid}
									instrumentBuilder={instrumentBuilder}
									minThreshold={MIN_NUMBER_OF_INVESTMENT_INSTRUMENTS}
									columns={initialColumns}
								/>
							) : (
								<InstrumentCompositionEditor
									entity="INVESTMENT"
									mode="new"
									instrumentBuilder={instrumentBuilder}
									minThreshold={MIN_NUMBER_OF_INVESTMENT_INSTRUMENTS}
									columns={initialColumns}
								/>
							)}
							<ExposureSankeyBlock
								expand={expandExposure}
								onExpandChange={setExpandExposure}
								instrumentBuilder={instrumentBuilder}
							/>
						</div>

						<CompareOverlay
							show={comparedInvestments.length > 0}
							onClose={() => instrumentBuilder.clearComparedInstruments()}
							onRemove={(id) => instrumentBuilder.deleteComparedInstruments(id)}
							compareData={comparedInvestments}
						/>
					</>
				)}
			</ReactQueryWrapperBase>
			<LeavePrompt
				title={t("NOTIFICATION_SETTINGS.LEAVE_PROMPT.TITLE")}
				when={instrumentBuilder.isDirty}
				pathToNotBlock={pathToNotBlock}
			>
				{t("NOTIFICATION_SETTINGS.LEAVE_PROMPT.MESSAGE")}
			</LeavePrompt>
		</>
	);
}

/**
 * Opens the weight editor, or the quantity one (MDOT-25812): a new composition is in quantities when its
 * link says so, a draft when its summary does.
 */
function ManualCreationPage(props: React.ComponentProps<typeof ManualCreation> & { quantityMode?: string }) {
	const { quantityMode, ...weightProps } = props;
	const { uuid, workspaceUid, intentId } = props;
	const querySummary = useQuery({
		...portfolioRepository.summary.getQueryConfig(uuid ?? "", { enhanced: false }),
		enabled: Boolean(uuid),
	});
	if (!uuid) {
		return quantityMode === "true" ? (
			<QuantityManualCreation workspaceUid={workspaceUid} intentId={intentId} />
		) : (
			<ManualCreation {...weightProps} />
		);
	}
	return (
		<ReactQueryWrapperBase query={querySummary}>
			{(summary) =>
				quantityMode === "true" || summary.quantityMode ? (
					<QuantityManualCreation
						key={uuid}
						workspaceUid={workspaceUid}
						intentId={intentId}
						draft={{ uuid, summary }}
					/>
				) : (
					<ManualCreation {...weightProps} />
				)
			}
		</ReactQueryWrapperBase>
	);
}

export default ManualCreationPage;
